import { useContext, useEffect, useState, forwardRef, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Stack,
  Card,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { ReactComponent as ListIcon } from "../../../assets/Icon-List.svg";
import { ReactComponent as IconPlay } from "../../../assets/IconPlay.svg";
import { ReactComponent as IconPlayFilled } from "../../../assets/IconPlayFilled.svg";

import { IconButtonContained } from "../utils/IconButtonContained";
import { IconButtonOutlined } from "../utils/IconButtonOutlined";
import { ModuleProgressIndicator } from "../utils/ModuleProgressIndicator";
import { CourseStructureContext } from "../../../context/CourseStructureContext";
import { UserContext } from "../../../context/UserContext";
import { CourseStructureCtx, UserCtx } from "../../../interfaces";
import { generateGtmEvent } from "../../../Helpers";
import { DigitStudyToolLessonCard } from "../module/DigitStudyToolLessonCard";
import { QuestionType, TARGET_COMPLETION_STATUS } from "../../../enums";
import { Question as QuestionTree, Question, Target } from "../../utils/CourseTree";
import { MacsAuthenticator } from "../../../hooks/useMacsAuthenticator";
import { _AuthType } from "../../../enums";

const useStyles = makeStyles((theme) => ({
  accordionRoot: {
    padding: "0px",
    backgroundColor: "transparent",
    overflow: "hidden",
    boxSizing: "border-box",
    margin: "0px !important",
    "& .MuiAccordionSummary-root": {
      minHeight: "unset !important",
    },
  },
  accordionSummary: {
    margin: "0px !important",
    padding: "24px",
    borderRadius: "8px",
    "& .MuiAccordionSummary-content": {
      margin: "0px !important",
    },
  },
  accordionHeading: {
    fontWeight: 700,
    lineHeight: "24px",
    color: theme.palette.text.primary,
    [theme.breakpoints.down("md")]: {
      fontSize: "16px",
      lineHeight: "26px",
    },
  },
  accordionGroupSummary: {
    color: "#474747",
    fontWeight: 400,
    textWrap: "pretty",
    overflowWrap: "anywhere",
    [theme.breakpoints.down("md")]: {
      fontSize: "16px",
      lineHeight: "26px",
    },
  },
  expandChevronWrapper: {
    fontSize: "20px",
    height: "40px",
    width: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    "&:hover": {
      backgroundColor: "#DEDEDE",
      cursor: "pointer",
    },
  },

  expandChevron: {
    height: "24px",
    width: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.primary,
  },
  lockStatusIndicator: {
    padding: "4px 8px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "24px",
    backgroundColor: "#E8E8E8",
    color: "#484848",
  },
}));
interface ModuleOverview {
  index: number;
  moduleId: string;
  moduleName: string;
  moduleOrder: number;
  percentComplete: number;
  resumeAtQuestionId: string | undefined;
  isDemoLocked?: boolean;
  isModuleLocked?: boolean;
  moduleTabIndex: number;
  dropdownTabIndex: number;
  isActive?: boolean;
  showModuleNumber?: boolean;
}

export const ModuleListCard = forwardRef<HTMLDivElement, ModuleOverview>((props, ref) => {
  const classes = useStyles();
  const history = useHistory();
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const userCtx = useContext(UserContext) as UserCtx;
  const courseMetadata =
    courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata();
  const useDigitStudyTool = courseMetadata?.use_digit_study_tool ?? false;

  const [moduleExpanded, setModuleExpanded] = useState(props.isActive ?? false);

  const [userTargetResumeMap, setUserTargetResumeMap] = useState<Map<string, Question>>();

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const lockIconRef = useRef(null);

  let createGtmEvent = (section: string, text: string) => {
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "main content",
      section: section,
      text: text,
    });
  };

  const handleViewModuleClick = () => {
    const targetElement = document.getElementsByTagName("header");
    if (targetElement.length > 0) {
      targetElement[0].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }

    history.push({
      pathname: "/modules",
      search: "?moduleId=" + props.moduleId,
    });
    courseStructureCtx.setBackBehavior({ backTo: "moduleoverview" });
  };

  const handleResumeModuleClick = () => {
    if (!props.resumeAtQuestionId) {
      handleViewModuleClick();
      return;
    }
    courseStructureCtx.setBackBehavior({ backTo: "home" });

    history.push({
      pathname: "/solver",
      search: "&questionId=" + props.resumeAtQuestionId,
    });
  };

  const handleChevronClick = (event: any) => {
    event.stopPropagation();
    setModuleExpanded((prev) => !prev);
  };

  const currentModule = courseStructureCtx.courseTreeStructure?.getModule(props.moduleId);

  useEffect(() => {
    const userTargetResumeMap = new Map();
    currentModule?.children.forEach((target) => {
      // fall back to mastery problem if no problem to resume on
      // or go to mastery if its incorrect
      // AKA all problems complete
      // const masteryProblem = courseStructureCtx.targetMap[target.id].questions.find(
      //   (question: Question) => {
      //     return question.type === QuestionType.mastery;
      //   }
      // );
      const masteryProblem = target
        .getTargetQuestions()
        .find((question: QuestionTree) => {
          return question.type === QuestionType.mastery;
        });

      const masteryProblemIncorrect =
        masteryProblem &&
        masteryProblem.is_question_submitted &&
        !userCtx.checkPerfectScore(masteryProblem.id);

      // construct map entry
      // fall back to mastery problem if there's nothing to resume
      // if there's a mastery submission and its incorrect, resume there
      userTargetResumeMap.set(
        target.id,
        masteryProblemIncorrect
          ? masteryProblem
          : userCtx.getProblemToResumeOnTarget(target.id) ?? masteryProblem
      );
      // construct skill map entry
      //userTargetSkillsMap.set(target.id, courseStructureCtx.targetMap[target.id].skills);
    });
    setUserTargetResumeMap(userTargetResumeMap);
  }, [
    userCtx.getProblemToResumeOnTarget,
    currentModule?.children,
    userCtx,
    props.moduleId,
  ]);

  // get the first started but incomplete target, otherwise get the first non started target
  const emphasizedLesson = useMemo(() => {
    if (!props.isActive && props.isModuleLocked) return null;
    const targets = (currentModule?.children ?? []) as Target[];
    const inProgressTarget = targets.find(
      (item) => item.getTargetCompletionStatus() === TARGET_COMPLETION_STATUS.IN_PROGRESS
    );
    const notStartedTarget = targets.find(
      (item) => item.getTargetCompletionStatus() === TARGET_COMPLETION_STATUS.NOT_STARTED
    );
    return inProgressTarget ?? notStartedTarget ?? null;
  }, [currentModule?.children, props.isActive, props.isModuleLocked]);

  return (
    <>
      {useDigitStudyTool && useDigitStudyTool === true ? (
        <Card
          ref={ref}
          sx={{
            marginBottom: "24px",
            "&:hover": {
              boxShadow: props.isModuleLocked ? "none" : "0px 8px 12px #00000029",
            },
          }}>
          <Accordion
            key={props.index}
            className={classes.accordionRoot}
            expanded={moduleExpanded}>
            <AccordionSummary
              onMouseEnter={() => props.isModuleLocked && setTooltipOpen(true)}
              onMouseLeave={() => setTooltipOpen(false)}
              sx={{
                height: "112px",
                cursor: props.isModuleLocked
                  ? "default !important"
                  : "pointer !important",
              }}
              className={classes.accordionSummary}
              tabIndex={props.moduleTabIndex}
              aria-label={`Module ${props.moduleOrder} ${props.moduleName}`}
              expandIcon={
                <Box
                  className={classes.expandChevronWrapper}
                  tabIndex={props.dropdownTabIndex}
                  aria-label={`Toggle Module- ${props.index + 1} Accordion`}
                  onClick={handleChevronClick}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleChevronClick(e);
                    }
                  }}>
                  <i className={`${classes.expandChevron} fa-solid fa-chevron-down`}></i>
                </Box>
              }
              onClick={() => {
                if (!props.isModuleLocked) handleViewModuleClick();
              }}>
              <Stack direction="column" spacing={2}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  {props.showModuleNumber && (
                    <Typography
                      component="span"
                      variant="body2"
                      fontSize="16px"
                      className={classes.accordionGroupSummary}
                      textTransform="uppercase">
                      {`Module ${props.moduleOrder}`}
                    </Typography>
                  )}
                  {props.isModuleLocked && (
                    <Tooltip
                      open={tooltipOpen}
                      title={
                        <span>
                          Head back to{" "}
                          {MacsAuthenticator.authHook.authType === _AuthType.LTI &&
                          MacsAuthenticator.authHook.ltiReturnUrl ? (
                            <Link
                              href={MacsAuthenticator.authHook.ltiReturnUrl}
                              underline="always"
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: "white",
                                textDecorationColor: "white",
                                "&:hover": {
                                  color: "#0092E0",
                                  textDecorationColor: "#0092E0",
                                },
                              }}
                              onClick={(e) => e.stopPropagation()}>
                              Canvas
                            </Link>
                          ) : (
                            "Canvas"
                          )}{" "}
                          to launch this module.
                        </span>
                      }
                      placement="right"
                      arrow>
                      <Box ref={lockIconRef} className={classes.lockStatusIndicator}>
                        <i
                          className="fa-solid fa-lock"
                          style={{ marginRight: "8px" }}></i>
                        <Typography variant="body1" lineHeight="1em">
                          LOCKED
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                </Stack>

                <Typography
                  component="span"
                  variant="h4"
                  className={classes.accordionHeading}
                  sx={{
                    "&:hover": {
                      textDecoration: props.isModuleLocked ? "none" : "underline",
                    },
                  }}>
                  {props.moduleName}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: "0px 20px ",
                marginBottom: "24px",
              }}>
              {currentModule?.children.map((target, index) => {
                const targetQuizScores = userCtx.userQuizSubmissions.filter(
                  (item) => item.target_id === target.id
                );
                return (
                  <DigitStudyToolLessonCard
                    key={index}
                    target={target}
                    status={target.getTargetCompletionStatus()}
                    targetQuizSubmissions={targetQuizScores}
                    resumeQuestion={userTargetResumeMap?.get(target.id)}
                    emphasize={
                      target.id === emphasizedLesson?.id && !target.is_asset_locked
                    }
                    parentLockStatus={currentModule.is_asset_locked}
                  />
                );
              })}
            </AccordionDetails>
          </Accordion>
        </Card>
      ) : (
        <Card
          sx={{
            marginBottom: "24px",
            minWidth: "280px",
            borderRadius: "15px",
            padding: "2rem",
          }}>
          <Stack direction="row">
            {/* text and progress */}
            <Box sx={{ position: "relative", flexGrow: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minWidth: "75%",
                }}>
                <Typography variant="body2" sx={{ mb: "1em" }} textTransform="uppercase">
                  Module
                </Typography>
                <Typography variant="h3" sx={{ mb: "24px" }}>
                  {props.moduleName}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  justifyContent: "flex-start",
                }}>
                <ModuleProgressIndicator
                  moduleId={props.moduleId}
                  percentage={props.percentComplete}
                  moduleName={props.moduleName}
                  progressBarSx={{ position: "relative", flexGrow: 1, maxWidth: "342px" }}
                  percentageSx={{ whiteSpace: "nowrap" }}
                />
              </Box>
            </Box>
            {/* controls */}
            <Box
              sx={{
                marginLeft: "auto",
                display: "flex",
                gap: "1.5rem",
                alignItems: "center",
              }}>
              <IconButtonOutlined
                size="large"
                disabled={props.isDemoLocked}
                aria-label={"Learning targets and videos"}
                tooltipLabel={"Learning targets and videos"}
                tooltipPlacement="bottom"
                id={props.index === 0 ? "module-page-button" : undefined}
                onClick={() => {
                  createGtmEvent(props.moduleName, "learning targets and videos button");
                  handleViewModuleClick();
                }}>
                <ListIcon
                  style={{
                    height: "28px",
                    width: "28px",
                  }}
                />
              </IconButtonOutlined>
              <IconButtonContained
                zoom={true}
                disabled={props.isDemoLocked}
                size="large"
                aria-label={"Practice"}
                tooltipLabel={"Practice"}
                tooltipPlacement="bottom"
                id={props.index === 0 ? "practice-page-button" : undefined}
                onClick={() => {
                  createGtmEvent(props.moduleName, "practice play button");
                  handleResumeModuleClick();
                }}
                hover={<IconPlayFilled style={{ height: "30px", width: "30px" }} />}>
                <IconPlay style={{ height: "30px", width: "30px" }} />
              </IconButtonContained>
            </Box>
          </Stack>
        </Card>
      )}
    </>
  );
});
